import { useSelector, useDispatch } from "react-redux";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Avater from "../../components/Avater/Avater";
import Button from "../../components/Button/Button";
import { RiEdit2Fill } from "react-icons/ri";
import { RootState, AppDispatch } from "../../store";
import { CiLocationOn } from "react-icons/ci";
import { MdDomainVerification } from "react-icons/md";
import { MdEmail } from "react-icons/md";
import { CiPhone } from "react-icons/ci";
import { updateProfile } from "../../store/auth/updateProfileThunk";
import { useGetUserProfileQuery } from "../../store/auth/fetchUserProfileThunk";
import { toast } from "react-toastify";
import schema from "../../utils/validations/Profile";
import styles from "./Profile.module.css";
import { NavLink } from "react-router";
import { useGetUserListingsQuery } from "../../store/listing/fetchListingsforUser";
import CardLayout from "../../Layout/CardLayout";
import ClipLoader from "react-spinners/esm/ClipLoader";
import loadingStyles from "../../shared-styles/loading.module.css";
interface ProfileFormData {
  userName: string;
  userLocation: string;
  phoneNumber: string;
}

export default function Profile() {
  const dispatch = useDispatch<AppDispatch>();
  const { uid, userName, userLocation, phoneNumber } = useSelector(
    (state: RootState) => state.auth.user
  );

  const {
    data: userProfile,
    isLoading: fetchUserProfileLoading,
    isError: fetchUserProfileError
  } = useGetUserProfileQuery({ uid, userName, userLocation, phoneNumber });

  const {
    data: listings,
    isLoading: fetchListingsLoading,
    isError: fetchListingsError
  } = useGetUserListingsQuery(uid === null ? "" : uid);

  const { updateProfileLoading, updateProfileError } = useSelector(
    (state: RootState) => state.auth.updateProfile
  );

  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ProfileFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      userName: userProfile?.fields?.userName?.stringValue || userName || "",
      userLocation:
        userProfile?.fields?.userLocation?.stringValue || userLocation || "",
      phoneNumber:
        userProfile?.fields?.phoneNumber?.stringValue || phoneNumber || ""
    },
    mode: "onChange"
  });

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
  };

  const onSubmit = async (data: ProfileFormData) => {
    try {
      const resultAction = await dispatch(updateProfile(data));

      if (updateProfile.fulfilled.match(resultAction)) {
        toast.success("Profile updated successfully!");
        setIsEditing(false);
        reset();
      } else if (updateProfile.rejected.match(resultAction)) {
        toast.error(
          resultAction.payload?.message || "Failed to update profile"
        );
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  // Show error state if fetching failed
  if (fetchUserProfileError) {
    // console.log("Error loading profile:", fetchUserProfileError);
    return (
      <main>
        <div className={`${styles.container} container`}>
          <div className={styles.header}>
            <h1>Profile</h1>
          </div>
          <div className={styles.error}>
            Error loading profile: {fetchUserProfileError}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      <div className={`${styles.container} container`}>
        <div className={styles.header}>
          <h1>Profile</h1>
          {!isEditing && !fetchUserProfileLoading ? (
            <Button
              type="button"
              icon={RiEdit2Fill}
              className="primary"
              onClick={handleEditClick}
            >
              Edit Profile
            </Button>
          ) : !isEditing && fetchUserProfileLoading ? (
            <div className={loadingStyles.loading}>
              <ClipLoader loading size={200} color="#35a257" />
            </div>
          ) : (
            <div className={styles.editActions}>
              <Button
                type="button"
                className="secondary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="primary"
                onClick={handleSubmit(onSubmit)}
                disabled={updateProfileLoading}
              >
                {updateProfileLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}
        </div>

        {!fetchUserProfileLoading && (
          <div>
            <div className={styles.box}>
              <Avater />

              {isEditing && fetchUserProfileLoading ? (
                <div className={loadingStyles.loading}>
                  <ClipLoader loading size={200} color="#35a257" />
                </div>
              ) : isEditing && !fetchUserProfileLoading ? (
                <div className={styles.editFields}>
                  <div className={styles.inputGroup}>
                    <label htmlFor="userName">Name</label>
                    <input
                      id="userName"
                      type="text"
                      placeholder="Enter your name"
                      {...register("userName")}
                      className={styles.editInput}
                    />
                    {errors.userName && (
                      <span className={styles.errorText}>
                        {errors.userName.message}
                      </span>
                    )}
                  </div>
                  <div className={styles.inputGroup}>
                    <label htmlFor="userLocation">Location</label>
                    <input
                      id="userLocation"
                      type="text"
                      placeholder="Enter your location"
                      {...register("userLocation")}
                      className={styles.editInput}
                    />
                    {errors.userLocation && (
                      <span className={styles.errorText}>
                        {errors.userLocation.message}
                      </span>
                    )}
                  </div>
                  <div className={styles.inputGroup}>
                    <label htmlFor="phoneNumber">Phone Number</label>
                    <input
                      id="phoneNumber"
                      type="tel"
                      placeholder="Enter your phone number"
                      {...register("phoneNumber")}
                      className={styles.editInput}
                    />
                    {errors.phoneNumber && (
                      <span className={styles.errorText}>
                        {errors.phoneNumber.message}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <h2>{userProfile?.fields?.userName?.stringValue}</h2>
                  <h3>
                    <CiLocationOn />
                    {userProfile?.fields?.userLocation?.stringValue ||
                      "unKown Address"}
                  </h3>
                  <span className={styles.online}>Online Now</span>
                  <span className={styles.verified}>
                    <MdDomainVerification /> Verified
                  </span>
                </>
              )}
            </div>

            {!isEditing && (
              <div className={styles.summary}>
                <p className={styles.totalListings}>
                  <span>Total Listings</span>
                  <span>{listings.length}</span>
                </p>
                <p className={styles.emailDetails}>
                  <MdEmail />{" "}
                  {userProfile?.fields?.email?.stringValue || "unkown Email"}
                </p>
                <p className={styles.phoneDetails}>
                  <CiPhone />
                  {userProfile?.fields?.phoneNumber?.stringValue || "unkown"}
                </p>
                <div className={styles.createListing}>
                  <NavLink to="/create-listing">+ Create Listing</NavLink>
                </div>
              </div>
            )}
          </div>
        )}

        {!isEditing && fetchListingsLoading ? (
          <div className={loadingStyles.loading}>
            <ClipLoader loading size={200} color="#35a257" />
          </div>
        ) : listings === undefined ||
          listings === null ||
          listings.length === 0 ? (
          <p>No Listings exists </p>
        ) : fetchListingsError ? (
          <p>Somthing went wrong </p>
        ) : listings.length > 0 ? (
          <CardLayout cardItems={listings} />
        ) : null}

        {updateProfileError && (
          <div className={styles.error}>{updateProfileError}</div>
        )}
      </div>
    </main>
  );
}
